import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Card, PageHeader } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { LANGS, useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { ChangePassword } from "@/components/app/ChangePassword";

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 rounded-xl" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Settings() {
  const { L, setLang } = useApp();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState({ name: user.name, ui_language: user.ui_language || "ms", transcription_language: user.transcription_language || "auto", output_language: user.output_language || "ms" });
  const save = async () => {
    try { await api.patch("/auth/me", p); setLang(p.ui_language); await refresh(); toast.success(L("Tetapan disimpan", "Settings saved")); } catch (e) { toast.error(formatApiError(e)); }
  };
  const exportData = async () => {
    const { data } = await api.get("/auth/export-data");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    a.download = "minitai-data-saya.json";
    a.click();
  };
  const del = async () => {
    try { await api.delete("/auth/me"); await logout(); navigate("/"); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-8" data-testid="settings-page">
      <PageHeader title={L("Tetapan", "Settings")} />
      <Card className="mt-6 space-y-4 p-6">
        <div className="space-y-1.5"><Label>{L("Nama", "Name")}</Label><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} className="h-10 rounded-xl" data-testid="settings-name-input" /></div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5"><Label>{L("Bahasa antara muka", "Interface language")}</Label><Pick value={p.ui_language} onChange={(v) => setP({ ...p, ui_language: v })} testId="settings-ui-lang" options={[["ms", "Bahasa Melayu"], ["en", "English"]]} /></div>
          <div className="space-y-1.5"><Label>{L("Bahasa transkripsi", "Transcription language")}</Label><Pick value={p.transcription_language} onChange={(v) => setP({ ...p, transcription_language: v })} testId="settings-transcription-lang" options={[["auto", "Auto"], ...LANGS.map(([k, ms, en]) => [k, L(ms, en)])]} /></div>
          <div className="space-y-1.5"><Label>{L("Bahasa hasil", "Output language")}</Label><Pick value={p.output_language} onChange={(v) => setP({ ...p, output_language: v })} testId="settings-output-lang" options={LANGS.map(([k, ms, en]) => [k, L(ms, en)])} /></div>
        </div>
        <Button onClick={save} className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="settings-save-btn">{L("Simpan", "Save")}</Button>
      </Card>
      <ChangePassword />
      <Card className="mt-6 p-6">
        <h2 className="font-semibold">{L("Data & privasi", "Data & privacy")}</h2>
        <p className="mt-1 text-sm text-slate-500">{L("Muat turun semua data anda atau padam akaun secara kekal.", "Download all your data or permanently delete your account.")}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="outline" className="rounded-xl" onClick={exportData} data-testid="export-my-data-btn">{L("Eksport data saya (JSON)", "Export my data (JSON)")}</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="rounded-xl text-red-600" data-testid="delete-account-btn">{L("Padam akaun", "Delete account")}</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>{L("Padam akaun secara kekal?", "Permanently delete account?")}</AlertDialogTitle><AlertDialogDescription>{L("Semua mesyuarat yang anda cipta, transkrip, minit dan chat akan dipadam.", "All meetings you created, transcripts, minutes and chats will be deleted.")}</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>{L("Batal", "Cancel")}</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-account-btn">{L("Padam", "Delete")}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>
    </div>
  );
}
